import { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader, type IScannerControls } from "@zxing/browser";
import { postScan } from "../../services/api";
import { useOfflineQueue } from "../../state/offlineQueue";
import { useOnlineStatus } from "../../hooks/useOnlineStatus";
import { addDemoScan } from "../../services/demoService";
import { useAuthStore } from "../../state/auth";
import { CheckCircle, Camera, CameraOff } from "lucide-react";
import { cn } from "../../lib/utils";

type ScanStatus = "idle" | "processing" | "verified" | "queued" | "error" | "scanned";

type LastLocation = { lat: number; lng: number; accuracy?: number } | null;

export function ScanPage() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const codeReaderRef = useRef<BrowserMultiFormatReader | null>(null);
  const scannerControlsRef = useRef<IScannerControls | null>(null);
  const isMountedRef = useRef<boolean>(false);
  const [scanValue, setScanValue] = useState<string | null>(null);
  const [status, setStatus] = useState<ScanStatus>("idle");
  const [message, setMessage] = useState<string>("");
  const [notes, setNotes] = useState("");
  const [lastLocation, setLastLocation] = useState<LastLocation>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [cameraActive, setCameraActive] = useState(false);
  const [manualControl, setManualControl] = useState(false);
  const [showSubmitForm, setShowSubmitForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const online = useOnlineStatus();
  const { enqueue, sync, syncing } = useOfflineQueue();
  const { user } = useAuthStore();

  const stopCamera = () => {
    setCameraActive(false);
    setIsInitializing(false);

    // Clear any success messages when stopping camera
    resetScan();

    if (scannerControlsRef.current) {
      try {
        scannerControlsRef.current.stop();
      } catch (err) {
        // Silently ignore stop errors
      }
      scannerControlsRef.current = null;
    }

    if (codeReaderRef.current) {
      try {
        // Reset the QR reader - some versions may not have reset method
        if (typeof (codeReaderRef.current as any).reset === "function") {
          (codeReaderRef.current as any).reset();
        }
      } catch (err) {
        // Silently ignore reset errors
      }
      codeReaderRef.current = null;
    }

    if (videoRef.current) {
      const existing = videoRef.current.srcObject as MediaStream | null;
      if (existing) {
        existing.getTracks().forEach((track) => track.stop());
      }
      videoRef.current.srcObject = null;
      try {
        videoRef.current.pause();
      } catch (err) {
        // Silently ignore pause errors
      }
    }
  };

  const startCamera = async () => {
    if (cameraActive) return;

    setIsInitializing(true);
    setCameraError(null);

    const reader = new BrowserMultiFormatReader();
    codeReaderRef.current = reader;

    const videoElement = videoRef.current;
    if (!videoElement) {
      setCameraError("Camera element not ready");
      setStatus("error");
      setIsInitializing(false);
      return;
    }

    try {
      const controls = await reader.decodeFromVideoDevice(undefined, videoElement, (result, err) => {
        if (!isMountedRef.current) return;
        if (result) {
          void handleScan(result.getText());
        }
      });

      if (!isMountedRef.current) {
        controls.stop();
        return;
      }

      scannerControlsRef.current = controls;
      setCameraActive(true);
      setIsInitializing(false);
    } catch (error) {
      if (!isMountedRef.current) return;
      console.error("Unable to start camera", error);
      setCameraError(`Camera access error: ${error instanceof Error ? error.message : String(error)}`);
      setStatus("error");
      setIsInitializing(false);
    }
  };

  // Initialize component but don't auto-start camera
  useEffect(() => {
    isMountedRef.current = true;
    setIsInitializing(false);

    return () => {
      isMountedRef.current = false;
      stopCamera();
    };
  }, []);

  useEffect(() => {
    if (!online) return;
    void sync();
  }, [online, sync]);

  // Auto-hide success overlay after 5 seconds
  useEffect(() => {
    if (status === "scanned") {
      const timer = setTimeout(() => {
        setStatus("idle");
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [status]);


  async function handleScan(token: string) {
    if (status === "processing" || !isMountedRef.current) return;
    setStatus("processing");
    setScanValue(token);
    setMessage("Processing scan…");

    let position: GeolocationPosition | null = null;
    try {
      position = await requestLocation();
    } catch (error) {
      // Location unavailable - will be handled by checking if position is null
    }

    if (position) {
      setLastLocation({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        accuracy: position.coords.accuracy
      });
    } else {
      setMessage("Location unavailable. Scan will be flagged for supervisor review.");
    }

    // Show success indicator and submission form
    setStatus("scanned");
    setMessage("QR Code scanned successfully! Add notes and submit.");
    setShowSubmitForm(true);

  }

  const handleSubmitScan = async () => {
    if (!scanValue || submitting) return;

    setSubmitting(true);
    setStatus("processing");
    setMessage("Submitting scan data...");

    const payload = {
      token: scanValue,
      clientTs: new Date().toISOString(),
      lat: lastLocation?.lat,
      lng: lastLocation?.lng,
      accuracy: lastLocation?.accuracy,
      notes: notes.trim().length ? notes.trim() : undefined,
      online
    };

    // Always save to demo service for client demos
    addDemoScan(
      scanValue,
      notes.trim() || undefined,
      user?.name || 'Demo Guard'
    );

    if (online) {
      try {
        await postScan(payload);
        setStatus("verified");
        setMessage("Scan saved successfully! Check supervisor dashboard to see your scan.");
        setNotes("");
        setShowSubmitForm(false);
        setSubmitting(false);

        // Auto-reset after 4 seconds to give time to read message
        setTimeout(() => {
          resetScan();
        }, 4000);
        return;
      } catch (error) {
        // Backend unavailable, fall back to demo mode
        setStatus("verified");
        setMessage("Scan saved successfully! Check supervisor dashboard to see your scan.");
        setNotes("");
        setShowSubmitForm(false);
        setSubmitting(false);

        // Auto-reset after 4 seconds
        setTimeout(() => {
          resetScan();
        }, 4000);
        return;
      }
    }

    await enqueue({
      checkpointId: scanValue, // Use the QR token as checkpoint ID
      token: scanValue,
      clientTs: payload.clientTs,
      lat: payload.lat,
      lng: payload.lng,
      accuracy: payload.accuracy,
      notes: payload.notes,
      online
    });

    setStatus("queued");
    setMessage("Saved offline. Will sync when connected.");
    setNotes("");
    setShowSubmitForm(false);
    setSubmitting(false);

    // Auto-reset after 3 seconds
    setTimeout(() => {
      resetScan();
    }, 3000);
  };

  const resetScan = () => {
    setStatus("idle");
    setMessage("");
    setScanValue(null);
    setLastLocation(null);
    setShowSubmitForm(false);
    setSubmitting(false);
  };

  return (
    <div className="p-6 max-w-md mx-auto">
      <div className="space-y-6">
        <header className="text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <h1 className="text-2xl font-bold text-gray-800">Scan Checkpoint</h1>
            {cameraActive ? (
              <Camera className="w-6 h-6 text-green-500" />
            ) : (
              <CameraOff className="w-6 h-6 text-gray-400" />
            )}
          </div>
          <p className="text-gray-600">
            Aim at the QR tag. We capture GPS automatically to verify presence.
          </p>
        </header>

        {/* Camera Control Button */}
        <div className="flex justify-center">
          <button
            onClick={cameraActive ? stopCamera : startCamera}
            disabled={isInitializing}
            className={cn(
              "flex items-center gap-2 px-6 py-3 text-sm rounded-md transition-opacity hover:opacity-90 disabled:opacity-50",
              cameraActive ? "bg-red-600 text-white" : "brand-green-gradient"
            )}
          >
            {isInitializing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Starting Camera...
              </>
            ) : cameraActive ? (
              <>
                <CameraOff className="w-5 h-5" />
                Stop Camera
              </>
            ) : (
              <>
                <Camera className="w-5 h-5" />
                Start Camera
              </>
            )}
          </button>
        </div>

        <div className="aspect-[3/4] overflow-hidden rounded-lg border-2 border-gray-300 bg-black relative">
          <video
            ref={videoRef}
            className="h-full w-full object-cover"
            muted
            playsInline
            autoPlay
            style={{ transform: 'scaleX(-1)' }}
          />

          {/* Loading overlay */}
          {isInitializing && (
            <div className="absolute inset-0 bg-black bg-opacity-75 flex items-center justify-center">
              <div className="text-white text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
                <p className="text-sm">Initializing camera...</p>
              </div>
            </div>
          )}

          {/* Camera error overlay */}
          {cameraError && (
            <div className="absolute inset-0 bg-red-600 bg-opacity-90 flex items-center justify-center">
              <div className="text-white text-center p-4">
                <p className="text-sm font-medium mb-2">Camera Error</p>
                <p className="text-xs">{cameraError}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="mt-3 px-3 py-1 bg-white text-red-600 text-xs rounded-md"
                >
                  Reload Page
                </button>
              </div>
            </div>
          )}

          {/* Success overlay */}
          {status === "scanned" && (
            <div className="absolute inset-0 bg-green-600 bg-opacity-90 flex items-center justify-center">
              <div className="text-white text-center">
                <CheckCircle className="w-16 h-16 mx-auto mb-2 animate-pulse" />
                <p className="text-lg font-medium">QR Code Scanned!</p>
                <p className="text-sm">Add notes and submit below</p>
              </div>
            </div>
          )}

          {/* Camera off overlay */}
          {!cameraActive && !isInitializing && !cameraError && (
            <div className="absolute inset-0 bg-gray-800 bg-opacity-90 flex items-center justify-center">
              <div className="text-white text-center">
                <CameraOff className="w-16 h-16 mx-auto mb-2 opacity-50" />
                <p className="text-lg font-medium">Camera Off</p>
                <p className="text-sm opacity-75">Press "Start Camera" to begin scanning</p>
              </div>
            </div>
          )}

          {/* Scanning frame */}
          {cameraActive && !isInitializing && !cameraError && status !== "scanned" && (
            <div className="absolute inset-0 border-4 border-transparent">
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-48 border-2 border-white opacity-50 rounded-lg"></div>
            </div>
          )}
        </div>

        {/* Manual QR Input for Testing */}
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Manual QR Code Input (Testing)</h3>
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Enter QR code manually"
              className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:ring-1 brand-ring-green brand-border-green focus:outline-none"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const input = e.target as HTMLInputElement;
                  if (input.value.trim()) {
                    void handleScan(input.value.trim());
                    input.value = '';
                  }
                }
              }}
            />
            <button
              onClick={() => {
                const input = document.querySelector('input[placeholder="Enter QR code manually"]') as HTMLInputElement;
                if (input?.value.trim()) {
                  void handleScan(input.value.trim());
                  input.value = '';
                }
              }}
              className="px-4 py-2 text-sm rounded-md transition-opacity hover:opacity-90 brand-green-gradient"
            >
              Submit
            </button>
          </div>
        </div>

        {/* Submission Form - shown after successful scan */}
        {showSubmitForm && (
          <div className="rounded-lg border border-green-200 bg-green-50 p-4">
            <h3 className="text-sm font-medium text-green-800 mb-3 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              QR Code Scanned: {scanValue}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  placeholder="Add any notes about this checkpoint, issues observed, or important details..."
                  className="w-full rounded-md border border-gray-300 bg-white p-3 text-sm focus:ring-1 brand-ring-green brand-border-green focus:outline-none"
                  rows={3}
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleSubmitScan}
                  disabled={submitting}
                  className="flex-1 py-2 px-4 text-sm rounded-md transition-opacity hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2 brand-green-gradient"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Submit Scan
                    </>
                  )}
                </button>

                <button
                  onClick={resetScan}
                  disabled={submitting}
                  className="px-4 py-2 text-gray-700 text-sm rounded-md border border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Regular notes field - shown when not in submission mode */}
        {!showSubmitForm && (
          <textarea
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder="Optional note for supervisors"
            className="w-full rounded-md border border-gray-300 bg-white p-3 text-sm focus:ring-1 brand-ring-green brand-border-green focus:outline-none"
            rows={3}
          />
        )}

        <ScanStatusBanner
          status={status}
          message={message}
          syncing={syncing}
          tokenValue={scanValue}
          lastLocation={lastLocation}
        />
      </div>
    </div>
  );
}

function ScanStatusBanner({
  status,
  message,
  tokenValue,
  syncing,
  lastLocation
}: {
  status: ScanStatus;
  message: string;
  tokenValue: string | null;
  syncing: boolean;
  lastLocation: LastLocation;
}) {
  if (status === "idle") {
    return (
      <p className="rounded-md border border-gray-300 bg-white p-3 text-sm text-gray-600">
        Ready to scan. Ensure lighting is adequate for QR recognition.
      </p>
    );
  }

  const styles: Record<ScanStatus, string> = {
    idle: "",
    processing: "bg-blue-100 text-blue-800 border-blue-200",
    verified: "bg-green-100 text-green-800 border-green-200",
    queued: "bg-amber-100 text-amber-800 border-amber-300",
    error: "bg-red-100 text-red-800 border-red-200",
    scanned: "bg-green-100 text-green-800 border-green-200"
  };

  return (
    <div className={`space-y-1 rounded-md border p-3 text-sm ${styles[status]}`}>
      <p className="font-medium capitalize">{status === "queued" ? "Saved offline" : status === "scanned" ? "QR Code Detected" : status}</p>
      <p>{message}</p>
      {tokenValue && <p className="text-xs opacity-75">Token: {tokenValue}</p>}
      {lastLocation && (
        <p className="text-xs opacity-75">
          GPS: {lastLocation.lat.toFixed(5)}, {lastLocation.lng.toFixed(5)}
          {lastLocation.accuracy ? ` ±${Math.round(lastLocation.accuracy)}m` : ""}
        </p>
      )}
      {syncing && <p className="text-xs">Syncing queued scans…</p>}
    </div>
  );
}

// Optimized location function for fast, accurate GPS readings
async function requestLocation(): Promise<GeolocationPosition | null> {
  if (!navigator.geolocation) {
    return null;
  }

  try {
    // Use watchPosition strategy for better accuracy without delays
    const position = await new Promise<GeolocationPosition>((resolve, reject) => {
      let bestPosition: GeolocationPosition | null = null;
      let attempts = 0;
      const maxAttempts = 2; // Fewer attempts for scanning (speed is critical)

      const watchId = navigator.geolocation.watchPosition(
        (pos) => {
          attempts++;

          // Keep the most accurate position
          if (!bestPosition || pos.coords.accuracy < bestPosition.coords.accuracy) {
            bestPosition = pos;
          }

          // Accept position if accurate enough or max attempts reached
          if (pos.coords.accuracy <= 30 || attempts >= maxAttempts) {
            navigator.geolocation.clearWatch(watchId);
            resolve(bestPosition);
          }
        },
        (error) => {
          navigator.geolocation.clearWatch(watchId);
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 6000, // Shorter timeout for scanning
          maximumAge: 10000 // Allow recent cached position for speed
        }
      );

      // Timeout fallback for scanning scenarios
      setTimeout(() => {
        navigator.geolocation.clearWatch(watchId);
        if (bestPosition) {
          resolve(bestPosition);
        } else {
          reject(new Error("GPS timeout during scan"));
        }
      }, 8000);
    });

    return position;

  } catch (error: any) {
    // GPS failed, trying fallback approach

    // Quick fallback for scanning
    try {
      const fallbackPosition = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          resolve,
          reject,
          {
            enableHighAccuracy: true,
            timeout: 4000,
            maximumAge: 30000 // Allow cached position for speed
          }
        );
      });

      return fallbackPosition;

    } catch (fallbackError: any) {
      if (fallbackError.code === fallbackError.PERMISSION_DENIED) {
        return null;
      }
      throw fallbackError;
    }
  }
}
